import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const AccessibleModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  className = '',
  closeOnEscape = true,
  closeOnOverlayClick = true,
  centerTitle = false,
  disableFocusTrap = false
}) => {
  const modalRef = useRef(null);
  const previousActiveElement = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;
      
      // Don't automatically focus anything - let the parent component handle it
      // This prevents interference with input field focus

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Handle escape key
      const handleEscape = (e) => {
        if (closeOnEscape && e.key === 'Escape') {
          onClose();
        }
      };

      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'unset';
        
        // Return focus to the previously focused element
        if (previousActiveElement.current) {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, onClose, closeOnEscape]);

  // Handle focus trap - only for buttons, not input fields
  useEffect(() => {
    if (!isOpen || !modalRef.current || disableFocusTrap) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button:not([disabled]), [href], select, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      // Don't interfere with input or textarea focus at all
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      // Only handle tab navigation between buttons and other non-input elements
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    // Add a small delay to ensure the modal is fully rendered
    const timeoutId = setTimeout(() => {
      document.addEventListener('keydown', handleTabKey);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('keydown', handleTabKey);
    };
  }, [isOpen, disableFocusTrap]);

  if (!isOpen) return null;

  const handleOverlayKeyDown = (e) => {
    if (closeOnOverlayClick && e.key === 'Enter') {
      onClose();
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      {closeOnOverlayClick && (
        <button
          className="absolute inset-0 w-full h-full cursor-default z-10"
          onClick={onClose}
          onKeyDown={handleOverlayKeyDown}
          aria-label="סגור חלון"
        />
      )}
      {/* eslint-disable-next-line */}
      <div
        ref={modalRef}
        className={`bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl relative z-20 pointer-events-auto ${className}`}
        tabIndex={-1}
        role="document"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="mb-4 relative">
            <h2 
              id="modal-title" 
              className={`text-xl font-bold text-gray-900 pr-10 ${centerTitle ? 'text-center' : ''}`}
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors absolute top-0 right-0"
              aria-label="סגור חלון"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
};

export default AccessibleModal;
